import { motion } from "framer-motion";
import { InstituteLogo } from "@/components/InstituteLogo";
import { LessonAttachments } from "@/components/LessonAttachments";
import type { LessonData } from "@/types/lesson";
import type { LessonAttachment } from "@/types/api";

interface FinishScreenProps {
  lesson: LessonData;
  onStartAgain: () => void;
  isChildTheme: boolean;
  attachments?: LessonAttachment[];
}

export function FinishScreen({ lesson, onStartAgain, isChildTheme, attachments = [] }: FinishScreenProps) {
  const handleDownloadPdf = async () => {
    const { downloadLessonSummaryPdf } = await import("@/lib/lessonSummaryPdf");
    downloadLessonSummaryPdf(lesson);
  };

  return (
    <motion.div
      className="flex flex-col items-center justify-center flex-1 p-8 text-center overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="mb-4 rounded-xl bg-white/80 p-2 shadow-md ring-1 ring-slate-200/80">
        <InstituteLogo height={isChildTheme ? 52 : 44} />
      </div>
      <motion.span
        className="text-6xl mb-4"
        animate={isChildTheme ? { scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] } : {}}
        transition={{ duration: 0.6 }}
      >
        🏆
      </motion.span>
      <h2
        className="text-2xl font-bold text-slate-800 mb-2"
        style={{ fontFamily: "var(--lp-font-heading)" }}
      >
        Lesson Complete!
      </h2>
      <p className="text-xs text-slate-500 max-w-sm mb-6">
        Download a short overview PDF based on your slides — handy for revision.
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-3 gap-y-3">
        <motion.button
          type="button"
          onClick={() => void handleDownloadPdf()}
          className="w-full sm:w-auto px-6 py-3 rounded-full font-semibold text-white shadow-lg border-2 border-white/30"
          style={{
            background: "linear-gradient(90deg, var(--lp-header-from), var(--lp-header-to))",
          }}
          whileHover={isChildTheme ? { scale: 1.03 } : undefined}
          whileTap={{ scale: 0.98 }}
        >
          Download lesson summary (PDF)
        </motion.button>
        <motion.button
          type="button"
          onClick={onStartAgain}
          className="w-full sm:w-auto px-6 py-3 rounded-full font-semibold text-slate-700 bg-white border-2 border-slate-200 shadow-md hover:bg-slate-50"
          whileHover={isChildTheme ? { scale: 1.03 } : undefined}
          whileTap={{ scale: 0.98 }}
        >
          Start Again
        </motion.button>
      </div>

      {attachments.length > 0 && (
        <div className="w-full mt-6">
          <LessonAttachments attachments={attachments} isChildTheme={isChildTheme} />
        </div>
      )}
    </motion.div>
  );
}
